import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription, catchError, forkJoin, of, take, takeUntil, timer } from 'rxjs';
import { UserAuthService } from '../auth.service';
import { SPECTRUM_LOCKUP, SPECTRUM_LOCKUP_DARK } from '../brand/spectrum-logo';
import { SpectrumLoginService } from './login.service';
import {
    SpectrumLaunchTarget, SpectrumLicencedModule, SpectrumLoginResponse, SpectrumLoginStep,
    SpectrumSchoolAccess
} from './login.model';

/**
 * The Spectrum sign-in screen: one door in front of every module.
 *
 * Four panels behind one component, driven by `step`:
 *   credentials  -> username + password, or the OTP hand-off
 *   otp          -> six-digit code
 *   newPassword  -> the auth API reported the password has expired
 *   schoolPicker -> the account has access to more than one school
 *
 * On success the screen hands off to `/validate` exactly the way the in-app
 * school switcher does, so a fresh sign-in and a school switch land the app
 * in the same state.
 */
@Component({
    selector: 'spectrum-login',
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './spectrum-login.component.html',
    styleUrl: './spectrum-login.component.scss'
})
export class SpectrumLoginComponent implements OnInit, OnDestroy {

    /** Shown in the brand panel. Override per deployment if the wording changes. */
    @Input() tagline = 'Simplifying Institution Management';

    readonly lockup = SPECTRUM_LOCKUP;
    readonly lockupDark = SPECTRUM_LOCKUP_DARK;

    /** Modules advertised on the brand panel. Purely presentational. */
    @Input() modules: SpectrumLicencedModule[] = [
        { name: 'Academics', icon: 'fa-book' },
        { name: 'Attendance', icon: 'fa-calendar-check' },
        { name: 'Finance', icon: 'fa-wallet' },
        { name: 'HR', icon: 'fa-users' },
        { name: 'Students', icon: 'fa-graduation-cap' },
        { name: 'Admissions', icon: 'fa-inbox' },
        { name: 'Institute', icon: 'fa-building-columns' },
        { name: 'Messages', icon: 'fa-comment-dots' },
        { name: 'Lesson Plans', icon: 'fa-clipboard-list' },
        { name: 'Security', icon: 'fa-key' }
    ];

    /** Where the parent/student portal lives, for the footer link. */
    @Input() parentPortalUrl = '';

    /**
     * Password-reset destination. Left empty the link is not rendered at all,
     * so the screen never offers a route that goes nowhere.
     */
    @Input() forgotPasswordUrl = '';

    /**
     * Where a successful sign-in goes. A module hosting this screen for itself
     * keeps the default; the portal sets `accessRoute` so each grant launches
     * into its own app.
     */
    @Input() launchTarget: SpectrumLaunchTarget = 'thisApp';

    /**
     * Also look up SmartParent grants, so a parent who is signed in with the
     * same account can open their children alongside their staff schools.
     */
    @Input() includeParentAccess = false;

    private fb = inject(FormBuilder);
    private loginService = inject(SpectrumLoginService);
    private auth = inject(UserAuthService);
    private route = inject(ActivatedRoute);
    private destroy$ = new Subject<void>();
    private resendTimer?: Subscription;

    step: SpectrumLoginStep = 'credentials';
    loading = false;
    errorMessage = '';
    showPassword = false;
    showNewPassword = false;

    credentialsForm!: FormGroup;
    passwordForm!: FormGroup;

    otpDigits: string[] = ['', '', '', '', '', ''];
    maskedMobile = '';
    resendSeconds = 0;

    schools: SpectrumSchoolAccess[] = [];
    students: SpectrumSchoolAccess[] = [];
    selectedSchool: SpectrumSchoolAccess | null = null;
    /** Filters the chooser. Accounts with many schools cannot scroll for theirs. */
    pickerSearch = '';

    /** Username carried between steps; the OTP and password-reset calls need it. */
    private username = '';

    ngOnInit(): void {
        this.credentialsForm = this.fb.group({
            username: ['', [Validators.required]],
            password: ['', [Validators.required]],
            rememberMe: [false]
        });

        this.passwordForm = this.fb.group({
            newPassword: ['', [Validators.required, Validators.minLength(8)]],
            confirmPassword: ['', [Validators.required]]
        });
    }

    ngOnDestroy(): void {
        this.resendTimer?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ---- Step 1: credentials -------------------------------------------

    signIn(): void {
        if (this.credentialsForm.invalid) {
            this.credentialsForm.markAllAsTouched();
            this.errorMessage = 'Enter your username and password.';
            return;
        }
        const { username, password } = this.credentialsForm.value;
        this.username = (username || '').trim();
        this.begin();
        this.loginService.login({ username: this.username, password })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => this.afterAuthentication(response),
                error: error => this.fail(error, 'Incorrect username or password.')
            });
    }

    // ---- Step 2: one-time code -----------------------------------------

    startOtp(): void {
        const username = (this.credentialsForm.get('username')?.value || '').trim();
        if (!username) {
            this.credentialsForm.get('username')?.markAsTouched();
            this.errorMessage = 'Enter your email or mobile first, then request a code.';
            return;
        }
        this.username = username;
        this.begin();
        this.loginService.sendOtp(username)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.loading = false;
                    // The send call returns no payload, so echo back what the
                    // user typed rather than inventing a masked number.
                    this.maskedMobile = username;
                    this.otpDigits = ['', '', '', '', '', ''];
                    this.step = 'otp';
                    this.startResendCountdown();
                },
                error: error => this.fail(error, 'We could not send a code to that account.')
            });
    }

    /** Keeps a single digit per box and moves focus along as the user types. */
    onOtpInput(index: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const digits = (input.value || '').replace(/\D/g, '');

        if (digits.length > 1) {
            // A paste — spread it across the remaining boxes.
            digits.split('').forEach((digit, offset) => {
                if (index + offset < this.otpDigits.length) {
                    this.otpDigits[index + offset] = digit;
                }
            });
            input.value = this.otpDigits[index];
            this.focusOtpBox(Math.min(index + digits.length, this.otpDigits.length - 1));
        } else {
            this.otpDigits[index] = digits;
            input.value = digits;
            if (digits && index < this.otpDigits.length - 1) {
                this.focusOtpBox(index + 1);
            }
        }

        if (this.otpCode.length === this.otpDigits.length) {
            this.verifyOtp();
        }
    }

    /** Backspace on an empty box steps back to the previous one. */
    onOtpKeydown(index: number, event: KeyboardEvent): void {
        if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
            this.focusOtpBox(index - 1);
        }
    }

    get otpCode(): string {
        return this.otpDigits.join('');
    }

    /**
     * The boxes hold their own DOM value, so the rows must never be re-keyed.
     * Without this Angular moves nodes as the array mutates and a typed digit
     * lands in the wrong box.
     */
    trackByIndex(index: number): number {
        return index;
    }

    verifyOtp(): void {
        if (this.otpCode.length !== this.otpDigits.length) {
            this.errorMessage = 'Enter all six digits.';
            return;
        }
        this.begin();
        this.loginService.verifyOtp(this.username, this.otpCode)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => this.afterAuthentication(response),
                error: error => {
                    this.otpDigits = ['', '', '', '', '', ''];
                    this.focusOtpBox(0);
                    this.fail(error, 'That code is not right, or it has expired.');
                }
            });
    }

    resendOtp(): void {
        if (this.resendSeconds > 0) return;
        this.startOtp();
    }

    backToPassword(): void {
        this.step = 'credentials';
        this.errorMessage = '';
    }

    // ---- Step 3: expired password --------------------------------------

    saveNewPassword(): void {
        const { newPassword, confirmPassword } = this.passwordForm.value;
        if (this.passwordForm.invalid) {
            this.passwordForm.markAllAsTouched();
            this.errorMessage = 'Choose a password of at least 8 characters.';
            return;
        }
        if (newPassword !== confirmPassword) {
            this.errorMessage = 'Both passwords must match.';
            return;
        }
        this.begin();
        this.loginService.changePassword(this.username, newPassword)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    // A password change may or may not return a fresh token; when it
                    // does not, the token from the original sign-in still stands.
                    if (response?.token) this.auth.saveToken(response.token);
                    this.loadSchools();
                },
                error: error => this.fail(error, 'We could not save that password.')
            });
    }

    /** 0-4, drives the strength meter. Length carries most of the weight. */
    get passwordStrength(): number {
        const value: string = this.passwordForm?.get('newPassword')?.value || '';
        if (!value) return 0;
        let score = 0;
        if (value.length >= 8) score++;
        if (value.length >= 12) score++;
        if (/\d/.test(value) && /[a-zA-Z]/.test(value)) score++;
        if (/[^a-zA-Z0-9]/.test(value)) score++;
        return score;
    }

    get passwordStrengthLabel(): string {
        return ['', 'Weak', 'Fair', 'Good', 'Strong'][this.passwordStrength] || '';
    }

    // ---- Step 4: which school ------------------------------------------

    /** The account the chooser is currently offering, shown so a shared machine is obvious. */
    get signedInAs(): string {
        return this.username;
    }

    get filteredSchools(): SpectrumSchoolAccess[] {
        return this.filterAccess(this.schools);
    }

    get filteredStudents(): SpectrumSchoolAccess[] {
        return this.filterAccess(this.students);
    }

    get hasPickerMatches(): boolean {
        return this.filteredSchools.length > 0 || this.filteredStudents.length > 0;
    }

    /** Matches on the school and on the person, since either is what is recognised. */
    private filterAccess(rows: SpectrumSchoolAccess[]): SpectrumSchoolAccess[] {
        const query = this.pickerSearch.trim().toLowerCase();
        if (!query) return rows;
        return rows.filter(row =>
            (row.schoolName || '').toLowerCase().includes(query)
            || (row.employeeName || '').toLowerCase().includes(query)
            || (row.studentName || '').toLowerCase().includes(query)
            || (row.orgCode || '').toLowerCase().includes(query));
    }

    onPickerSearch(value: string): void {
        this.pickerSearch = value;
        this.syncPickerSelection();
    }

    clearPickerSearch(): void {
        this.pickerSearch = '';
        this.syncPickerSelection();
    }

    /**
     * Keep the selection among the rows actually on screen. Without this,
     * filtering hides the selected row but leaves it selected, and Continue
     * opens a school the user can no longer see.
     */
    private syncPickerSelection(): void {
        const visible = [...this.filteredSchools, ...this.filteredStudents];
        if (!visible.length) {
            this.selectedSchool = null;
            return;
        }
        if (!this.selectedSchool || !visible.includes(this.selectedSchool)) {
            this.selectedSchool = visible[0];
        }
    }

    selectSchool(school: SpectrumSchoolAccess): void {
        this.selectedSchool = school;
    }

    /**
     * Drops the half-finished session and returns to the sign-in form. The token
     * is already stored by this point — someone who picked the wrong account, or
     * who walked away from a shared machine, needs a way out that actually
     * clears it rather than just going back a step.
     */
    signOut(): void {
        this.auth.logout();
        this.schools = [];
        this.students = [];
        this.selectedSchool = null;
        this.pickerSearch = '';
        this.otpDigits = ['', '', '', '', '', ''];
        this.credentialsForm.reset({ rememberMe: false });
        this.passwordForm.reset();
        // Both fields must go back to masked: sign-out returns to this same
        // component instance, so a toggle left on would hand the next person
        // on a shared machine a plain-text password field.
        this.showPassword = false;
        this.showNewPassword = false;
        this.errorMessage = '';
        this.loading = false;
        this.username = '';
        this.step = 'credentials';
    }

    enterSchool(school?: SpectrumSchoolAccess): void {
        const target = school || this.selectedSchool;
        if (!target || this.loading) return;
        this.selectedSchool = target;
        this.loading = true;
        this.handOff(target);
    }

    getInitials(name: string): string {
        if (!name) return '?';
        return name.trim().split(' ').filter(Boolean)
            .map(part => part[0]).join('').substring(0, 2).toUpperCase();
    }

    // ---- Shared ---------------------------------------------------------

    private afterAuthentication(response: SpectrumLoginResponse): void {
        if (!response?.token) {
            this.loading = false;
            this.errorMessage = 'Sign-in failed. Please try again.';
            return;
        }
        // Token first: the access lookup below is an authenticated call.
        this.auth.saveToken(response.token);
        this.username = response.username || this.username;

        if (response.isPasswordChangeRequired === 'Y') {
            this.loading = false;
            this.step = 'newPassword';
            return;
        }
        this.loadSchools();
    }

    private loadSchools(): void {
        this.loading = true;
        const staff$ = this.loginService.schoolAccess(this.username);
        const parent$ = this.includeParentAccess
            ? this.loginService.parentAccess(this.username).pipe(catchError(() => of([])))
            : of<SpectrumSchoolAccess[]>([]);

        // A support account with no school grants answers 406 rather than an
        // empty list, so a failed staff lookup is not fatal when the account
        // still has children to open.
        forkJoin({
            staff: staff$.pipe(catchError(() => of<SpectrumSchoolAccess[]>([]))),
            students: parent$
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ staff, students }) => {
                    this.schools = staff || [];
                    this.students = students || [];
                    const total = this.schools.length + this.students.length;

                    if (!total) {
                        this.reject('This account is not linked to any school. '
                            + 'If you are a support user, sign in through System Admin instead.');
                        return;
                    }
                    if (total === 1) {
                        this.handOff(this.schools[0] || this.students[0]);
                        return;
                    }
                    this.loading = false;
                    this.selectedSchool = this.schools[0] || this.students[0];
                    this.step = 'schoolPicker';
                },
                error: () => this.reject('We could not read your access. Contact your administrator.')
            });
    }

    /** True for a SmartParent grant, which opens a child rather than a school. */
    isStudent(access: SpectrumSchoolAccess): boolean {
        return this.students.includes(access);
    }

    /**
     * Store the session and hand off to `/validate`, mirroring the in-app school
     * switcher so both entry points leave the app in the same state.
     */
    private handOff(access: SpectrumSchoolAccess): void {
        const student = this.isStudent(access);
        this.auth.saveOrgCode(access.orgCode);
        this.auth.saveSchoolName(access.schoolName);
        this.auth.saveUserName(this.username);
        if (student) {
            this.auth.saveEmployeeName(access.studentName || '');
            this.auth.saveStudentProfileId(access.userUniqueId);
        } else {
            this.auth.saveEmployeeName(access.employeeName);
            this.auth.saveEmployeeId(access.userUniqueId);
        }
        if (access.appName) {
            this.auth.saveAppName(access.appName);
        }

        const token = this.auth.getToken() || '';

        // A module that bounced the user here for re-authentication passes its
        // own URL; it expects the session back as one base64 `query` value.
        const shortUrl = this.route.snapshot.queryParamMap.get('redirectURL') || '';
        if (shortUrl) {
            const payload = btoa(JSON.stringify({
                token,
                code: access.orgCode,
                appName: access.appName,
                employeeId: access.userUniqueId,
                employeeName: student ? access.studentName : access.employeeName,
                schoolName: access.schoolName,
                userName: this.username
            }));
            window.location.href =
                `${shortUrl}${shortUrl.includes('?') ? '&' : '?'}query=${encodeURIComponent(payload)}`;
            return;
        }

        // `accessRoute` launches into the app the grant names; `thisApp` stays
        // put, for a module that hosts this screen for itself.
        const origin = this.launchTarget === 'accessRoute' && access.route
            ? access.route.replace(/\/$/, '')
            : window.location.origin;

        const params = new URLSearchParams({
            token,
            code: access.orgCode || '',
            appName: access.appName || 'Spectrum',
            schoolName: access.schoolName || '',
            userName: this.username
        });
        if (student) {
            params.set('studentProfileId', access.userUniqueId || '');
            params.set('studentName', access.studentName || '');
            params.set('id', access.userUniqueId || '');
        } else {
            params.set('employeeId', access.userUniqueId || '');
            params.set('employeeName', access.employeeName || '');
        }

        const redirect = this.safeRedirect();
        if (redirect) params.set('redirect', redirect);

        window.location.href = `${origin}/validate?${params.toString()}`;
    }

    /** Only in-app absolute paths are honoured, never an external URL. */
    private safeRedirect(): string {
        const redirect = this.route.snapshot.queryParamMap.get('redirectURL')
            || this.route.snapshot.queryParamMap.get('redirect');
        if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
            return redirect;
        }
        return '';
    }

    private begin(): void {
        this.loading = true;
        this.errorMessage = '';
    }

    private fail(error: any, fallback: string): void {
        this.loading = false;
        this.errorMessage = error?.status === 401
            ? fallback
            : (error?.error?.message || fallback);
    }

    private reject(message: string): void {
        sessionStorage.clear();
        this.loading = false;
        this.step = 'credentials';
        this.errorMessage = message;
    }

    private startResendCountdown(): void {
        const seconds = 30;
        this.resendSeconds = seconds;
        // Replace rather than stack: requesting a second code must not run two
        // countdowns against the same counter.
        this.resendTimer?.unsubscribe();
        this.resendTimer = timer(1000, 1000)
            .pipe(take(seconds), takeUntil(this.destroy$))
            .subscribe(() => this.resendSeconds--);
    }

    private focusOtpBox(index: number): void {
        setTimeout(() => {
            const box = document.getElementById(`otp-${index}`) as HTMLInputElement | null;
            box?.focus();
            box?.select();
        });
    }
}
